/**
 * Pure helpers behind the TA board's email drafts and stage targets.
 * Kept out of the modal components so those files only export components
 * (React fast refresh) — and so this logic stays directly unit-testable.
 */
import type { FeedbackItem, StageRef } from '../../types/taActionBoard';

/** German du-form invite draft for the chosen target stage (Move modal). */
export function buildInviteEmailBody(
  candidateFirstName: string,
  stageName: string,
  senderFirstName: string
): string {
  return (
    `Hi ${candidateFirstName},\n\n` +
    `vielen Dank für das Gespräch! Wir freuen uns, dir mitteilen zu können, dass wir dich gerne ` +
    `zur nächsten Runde einladen möchten: ${stageName}.\n\n` +
    `Wir melden uns in Kürze mit Terminvorschlägen bei dir — falls du vorab Fragen hast, melde dich jederzeit.\n\n` +
    `Viele Grüße\n${senderFirstName}`
  );
}

/**
 * Target stages offered in the Move modal: every interview-ish Lever stage in
 * pipeline order, PLUS the computed next stage even when that is not
 * interview-ish (e.g. "Offer") so the automation's guess is always selectable.
 */
export function moveTargetStages(stages: StageRef[], item: FeedbackItem): StageRef[] {
  const list = stages.filter((s) => s.interviewish || s.id === item.nextStageId);
  if (item.nextStageId && !list.some((s) => s.id === item.nextStageId)) {
    list.push({ id: item.nextStageId, name: item.nextStageName, interviewish: false });
  }
  return list;
}

/**
 * German du-form slot proposal (Mark-offered modal, email path). Plain text —
 * it lands in the recruiter's own mail client as-is.
 */
export function buildOfferEmailBody({
  candidateFirstName,
  stageName,
  slotLines,
  senderFirstName,
  materialLink = null,
  language = 'de',
}: {
  candidateFirstName: string;
  stageName: string;
  slotLines: string[];
  senderFirstName: string;
  /**
   * The role's case material, when it is a shared LINK. It goes in the BODY
   * rather than as an attachment — which is also the only thing that works, as
   * the upload path has never been writable in production (see CaseMaterialRow).
   */
  materialLink?: { url: string; label: string } | null;
  /**
   * Follows the modal's DE/EN toggle. It used to be German unconditionally, so
   * choosing EN with the Email Library unreachable produced an English-labelled
   * modal holding a German draft — the same half-translated email the
   * final-round summary shipped until it was fixed.
   */
  language?: 'de' | 'en';
}): string {
  const de = language !== 'en';
  const bullets =
    slotLines.length > 0
      ? slotLines.map((l) => `- ${l}`).join('\n')
      : de
        ? '- (Termin ergänzen)'
        : '- (add a time)';
  // No em dashes in the German copy — an existing rule with a test behind it.
  const material = materialLink
    ? de
      ? `\n\nDie Unterlagen zum Case findest du hier:\n${materialLink.label}: ${materialLink.url}`
      : `\n\nYou can find the case material here:\n${materialLink.label}: ${materialLink.url}`
    : '';
  if (de) {
    return (
      `Hi ${candidateFirstName},\n\n` +
      `wir würden dich gerne zum ${stageName} einladen. Passt einer dieser Termine bei dir?\n\n` +
      `${bullets}` +
      `${material}\n\n` +
      'Schreib mir einfach kurz, welcher Termin passt, dann schicke ich dir die Einladung.\n\n' +
      `Viele Grüße\n${senderFirstName}`
    );
  }
  return (
    `Hi ${candidateFirstName},\n\n` +
    `we would like to invite you to the ${stageName}. Would any of these times work for you?\n\n` +
    `${bullets}` +
    `${material}\n\n` +
    'Just let me know which one suits you and I will send the invitation.\n\n' +
    `Best regards,\n${senderFirstName}`
  );
}

/**
 * Is this the Offer stage? Matched by Lever's own stage id first (this
 * instance uses the literal id "offer"), with a name check for instances that
 * renamed it. Deliberately narrow: "Offer declined" or a case stage must not
 * skip the invite-email flow.
 */
export function isOfferStage(stageId: string, stageName: string): boolean {
  if (`${stageId || ''}`.toLowerCase() === 'offer') return true;
  // Exactly "Offer" — `/^offer\b/` also matched "Offer declined", which would
  // have skipped the invite flow and opened Lever for a rejection.
  return /^offer$/i.test(`${stageName || ''}`.trim());
}
